import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Subject, formatSubjectName } from '@/types/questions';
import { Button } from '@/components/ui/button';
import { useAuth } from "@/contexts/AuthContext";
import { getUserData, setUserPremium } from "@/lib/firebase";
import type { UserData } from "@/lib/firebase";
import { PaywallModal } from "@/components/PaywallModal";
import { SEO } from '@/components/SEO';
import { ArrowLeft } from 'lucide-react';

const SubjectSelection = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [selectedSubject, setSelectedSubject] = useState<Subject | null>(null);
  const [userData, setUserData] = useState<UserData | null>(null);
  const [showPaywall, setShowPaywall] = useState(false);
  const [selectedYear, setSelectedYear] = useState('2015');
  const years = ['2015', '2016', '2017', '2018'];

  // Default to 'study' if location.state is null
  const mode = location.state?.mode || 'study';
  
  console.log('Subject Selection - Current mode:', mode);

  const displaySubjects = ['Mathematics', 'Chemistry', 'Physics', 'English', 'Biology'];
  const subjects: Subject[] = displaySubjects.map(formatSubjectName);

  useEffect(() => {
    const fetchUserData = async () => {
      if (user) {
        try {
          const data = await getUserData(user.uid);
          console.log('Fetched user data:', data); // Debug log
          
          // Force premium status to be a boolean
          const isPremiumUser = Boolean(data?.isPremium);
          
          // Set user data with explicit premium status
          const updatedData = {
            ...data,
            isPremium: isPremiumUser,
            questionAttempts: data?.questionAttempts || 0  // Provide default value
          };
          
          console.log('Setting user data with premium:', updatedData); // Debug log
          setUserData(updatedData);

          // Ensure paywall is closed for premium users
          if (isPremiumUser) {
            setShowPaywall(false);
          }
        } catch (error) {
          console.error('Error fetching user data:', error);
        }
      }
    };
    fetchUserData();
  }, [user]);

  const handleSubjectSelect = (subject: Subject) => {
    setSelectedSubject(subject);
  };

  const handleYearSelect = (year: string) => {
    setSelectedYear(year);
    
    if (userData?.isPremium) {
      // Premium users can select any year
      return;
    }

    // Only show paywall for non-premium users on locked years
    if (year !== '2015') {
      setShowPaywall(true);
    }
  };

  const handleStartQuiz = () => {
    if (selectedSubject) {
      // Premium users can always start quiz for any year
      if (userData?.isPremium) {
        navigate('/quiz', { 
          state: { 
            subject: selectedSubject,
            mode: mode,
            year: selectedYear
          } 
        });
        return;
      }

      // For non-premium users, show paywall for locked years
      if (selectedYear !== '2015') {
        setShowPaywall(true);
        return;
      }
      
      // Free year (2015) for non-premium users
      navigate('/quiz', { 
        state: { 
          subject: selectedSubject,
          mode: mode,
          year: selectedYear
        } 
      });
    }
  };

  const handlePaymentSuccess = async (response: any) => {
    if (!user?.uid) return;
    
    try {
      // Format payment data to match Firebase expectations
      const paymentData = {
        ...response,
        userEmail: user.email || '',
        timestamp: new Date(), // Send Date object directly
        isPremium: true,
        status: 'success'
      };

      // Update premium status in Firebase
      await setUserPremium(user.uid, paymentData);
      
      // Update local state immediately
      setUserData(prevData => ({
        ...prevData,
        isPremium: true,
        questionAttempts: prevData?.questionAttempts || 0,  // Provide default value
        paymentHistory: prevData?.paymentHistory || []
      }));
      
      // Close paywall
      setShowPaywall(false);
    } catch (error) {
      console.error('Error updating premium status:', error);
    }
  };

  // Add this useEffect to ensure premium status is always checked
  useEffect(() => {
    if (userData?.isPremium) {
      // Make sure paywall is closed and years are unlocked for premium users
      setShowPaywall(false);
    }
  }, [userData?.isPremium]);

  const handleModalClose = () => {
    const isPremiumUser = Boolean(userData?.isPremium);
    setShowPaywall(false);
    
    // Only reset to 2015 if user is not premium
    if (!isPremiumUser) {
      setSelectedYear('2015');
    }
  };

  const renderYearButton = (year: string) => {
    const isPremiumUser = Boolean(userData?.isPremium);
    const isSelected = selectedYear === year;
    const isFreeYear = year === '2015';

    return (
      <Button
        key={year}
        onClick={() => handleYearSelect(year)}
        variant={isSelected ? "default" : "outline"}
        className={`
          relative px-6 py-2 transition-all
          ${isSelected ? 'bg-[#7C3AED] text-white hover:bg-[#7C3AED]/90' : 'bg-white'}
          ${isPremiumUser ? 'hover:border-purple-200' : !isFreeYear ? 'opacity-50 hover:opacity-75' : 'hover:border-purple-200'}
        `}
      >
        <div className="flex items-center gap-2">
          {year}
          {!isPremiumUser && !isFreeYear && <span>🔒</span>}
        </div>
        {isPremiumUser && !isFreeYear && (
          <span className="absolute -top-2 -right-2 text-[10px] bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded-full font-medium">
            Unlocked
          </span>
        )}
      </Button>
    );
  };

  return (
    <>
      <SEO 
        title={`${mode === 'exam' ? 'Exam' : 'Study'} Mode - UTME Quiz`}
        description={`Practice JAMB UTME questions in ${mode} mode. Choose from subjects like Mathematics, English, Physics, Chemistry, and Biology.`}
        keywords={`JAMB ${mode} mode, UTME practice, Mathematics, English, Physics, Chemistry, Biology, exam preparation`}
      />
      <div className="flex flex-col items-center px-4 pt-[40px] sm:pt-[60px]">
        <div className="self-start mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate('/test-mode')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft size={20} />
            Back to Test Mode
          </Button>
        </div>

        <div className="text-center mb-12">
          <h1 className="text-[48px] font-bold text-black mb-2">Select Subject</h1>
          <p className="text-[#6B7280] text-xs sm:text-sm px-2">
            {mode === 'exam' 
              ? 'Exam Mode: Score shown after completing all questions' 
              : 'Study Mode: Immediate feedback for each question'}
          </p>
        </div>

        {/* Subject Selection Single Row */}
        <div className="flex flex-nowrap overflow-x-auto gap-4 mb-8 sm:mb-16 pb-4 max-w-full">
          {displaySubjects.map((displayName, index) => (
            <Button
              key={subjects[index]}
              onClick={() => handleSubjectSelect(subjects[index])}
              variant={selectedSubject === subjects[index] ? "default" : "outline"}
              className={`
                flex-shrink-0
                w-[120px] sm:w-[140px] h-[120px] sm:h-[140px] rounded-2xl
                flex flex-col items-center justify-center gap-2
                ${selectedSubject === subjects[index] 
                  ? 'bg-[#7C3AED] text-white hover:bg-[#7C3AED]/90' 
                  : 'hover:border-purple-200'
                }
              `}
            >
              <span className="text-2xl">
                {displayName === 'Mathematics' && '📐'}
                {displayName === 'English' && '📚'}
                {displayName === 'Physics' && '⚡'}
                {displayName === 'Chemistry' && '🧪'}
                {displayName === 'Biology' && '🧬'}
              </span>
              <span className="text-sm sm:text-base font-medium">{displayName}</span>
            </Button>
          ))}
        </div>

        {/* Year Selection */}
        <div className="flex flex-wrap justify-center gap-4 mb-8">
          {years.map((year) => renderYearButton(year))}
        </div>

        {/* Start Quiz Button - Show for premium users or when 2015 is selected */}
        {selectedSubject && (userData?.isPremium || selectedYear === '2015') && (
          <Button
            onClick={handleStartQuiz}
            className="h-12 sm:h-14 px-8 bg-[#7C3AED] hover:bg-[#7C3AED]/90"
          >
            Start {mode === 'exam' ? 'Exam' : 'Quiz'}
          </Button>
        )}

        {showPaywall && (
          <PaywallModal
            isOpen={showPaywall}
            onClose={handleModalClose}
            onPaymentSuccess={handlePaymentSuccess}
            userEmail={user?.email || ''}
          />
        )}
      </div>
    </>
  );
};

export default SubjectSelection; 